import { useEffect, useRef, useCallback } from 'react';
import { isOpenChainlit } from '../../../lib/utils';

interface ChainlitBotProps {
  userId: string | number;
  token: string;
  onWidgetStateChange?: (isOpen: boolean) => void;
  enableBackgroundClose?: boolean;
  enableLogout?: boolean;
}

const ChainlitBot = ({ 
  userId, 
  token, 
  onWidgetStateChange,
  enableBackgroundClose = true,
  enableLogout = true 
}: ChainlitBotProps) => {
  // State refs
  const isWidgetMountedRef = useRef(false);
  const currentUserIdRef = useRef(userId);
  const currentTokenRef = useRef(token);
  const mountAttemptsRef = useRef(0);
  const backgroundClickListenerRef = useRef<((event: MouseEvent) => void) | null>(null);
  const mountTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Logging utility
  const log = useCallback((level: 'info' | 'warn' | 'error') => {
    
    
    switch (level) {
      case 'info':
        break;
      case 'warn':
        break;
      case 'error':
        break;
    }
  }, []);

  const inspectWidgetDOM = useCallback(() => {
    
   
    
  
    // Check for iframes
   
  }, []);
  const validateConfiguration = useCallback(() => {
    const issues = [];
    
    if (!import.meta.env.VITE_CHATBOT_URL) {
      issues.push('VITE_CHATBOT_URL environment variable not set');
    }
    
    if (!token || token.trim() === '') {
      issues.push('Access token is required but not provided');
    }
    
    if (!userId) {
      issues.push('User ID is required but not provided');
    }

    if (issues.length > 0) {
      return false;
    }
    
   
    
    return true;
  }, [token, userId, log]);

  const isWidgetVisible = useCallback(() => {
    
    // Strategy 1: Check for chat modal/dialog elements
    const chatModalSelectors = [
      '#chainlit-copilot [role="dialog"]',
      '#chainlit-copilot .modal',
      '#chainlit-copilot .chat-interface',
      '#chainlit-copilot .chat-container',
      '#chainlit-copilot iframe',
      '[data-testid="chainlit-chat"]',
      '.chainlit-chat-open',
      '.chainlit-modal-open'
    ];

    let chatModalFound = false;
    for (const selector of chatModalSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        const style = window.getComputedStyle(element);
        const isVisible = style.display !== 'none' && 
                         style.visibility !== 'hidden' && 
                         style.opacity !== '0' &&
                         !element.hasAttribute('hidden');
        
        if (isVisible) {
          chatModalFound = true;
          break;
        } else {
         break; 
        }
      }
    }

    // Strategy 2: Check for CSS classes indicating open state
    const stateIndicators = [
      '#chainlit-copilot[data-state="open"]',
      '#chainlit-copilot.open',
      '#chainlit-copilot.expanded',
      '#chainlit-copilot.active',
      'body.chainlit-modal-open',
      'html.chainlit-open'
    ];

    let stateIndicatorFound = false;
    for (const selector of stateIndicators) {
      if (document.querySelector(selector)) {
        stateIndicatorFound = true;
        break;
      }
    }

    // Strategy 3: Check main widget container properties
    const mainWidget = document.getElementById('chainlit-copilot');
    let mainWidgetVisible = false;
    
    if (mainWidget) {
      const style = window.getComputedStyle(mainWidget);
      const rect = mainWidget.getBoundingClientRect();
      
      // Check if widget takes up significant screen space (indicating chat is open)
      const isLargeWidget = rect.width > 300 || rect.height > 400;
      
      mainWidgetVisible = style.display !== 'none' && 
                         style.visibility !== 'hidden' && 
                         style.opacity !== '0';

      

      // If widget is large, it's probably the open chat interface
      if (isLargeWidget && mainWidgetVisible) {
        chatModalFound = true;
      }
    } else {
      return false;
    }

    // Strategy 4: Check for overlay/backdrop elements
    const overlaySelectors = [
      '.chainlit-overlay',
      '.chainlit-backdrop',
      '[data-testid="chainlit-overlay"]'
    ];

    let overlayFound = false;
    for (const selector of overlaySelectors) {
      const overlay = document.querySelector(selector);
      if (overlay) {
        const style = window.getComputedStyle(overlay);
        if (style.display !== 'none' && style.opacity !== '0') {
          overlayFound = true;
          break;
        }
      }
    }

    // Strategy 5: Check ARIA expanded state
    const ariaExpandedElements = document.querySelectorAll('#chainlit-copilot [aria-expanded="true"]');
    const hasAriaExpanded = ariaExpandedElements.length > 0;
    
    if (hasAriaExpanded) {
    }

    // Final determination
    const isOpen = chatModalFound || stateIndicatorFound || overlayFound || hasAriaExpanded;
    
   

    return isOpen;
  }, [log]);

  // Safe widget toggle using official API
  const toggleWidget = useCallback(() => {
    
    // Pre-toggle state check
    const preToggleVisible = isWidgetVisible();
    
    if (!window.toggleChainlitCopilot) {
      return false;
    }

    if (!isWidgetMountedRef.current) {
      return false;
    }

    try {
      // Call the toggle function
      window.toggleChainlitCopilot();
      
      // Check state after a brief delay to let animations complete
      setTimeout(() => {
        const postToggleVisible = isWidgetVisible();
        
        // Verify the toggle actually worked
        if (preToggleVisible === postToggleVisible) {
          // Run DOM inspection to understand why
          inspectWidgetDOM();
        } else {
        }
        
        // Notify parent component of state change
        onWidgetStateChange?.(postToggleVisible);
      }, 500); // Wait for animations
      
      return true;
    } catch (error) {
      return false;
    }
  }, [log, onWidgetStateChange, isWidgetVisible, inspectWidgetDOM]);

  // Clean widget and reset state
  const cleanupWidget = useCallback(() => {
    
    // Clear any pending timeouts
    if (mountTimeoutRef.current) {
      clearTimeout(mountTimeoutRef.current);
      mountTimeoutRef.current = null;
    }

    // Try to close widget if it's open
    if (isWidgetMountedRef.current && isWidgetVisible()) {
      toggleWidget();
    }

    // Remove DOM elements as fallback
    const elementsToRemove = [
      '#chainlit-copilot',
      '[id^="chainlit"]',
      '[class*="chainlit"]',
      'iframe[src*="chainlit"]'
    ];

    elementsToRemove.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          elements.forEach(element => element.remove());
        }
      } catch (error) {
      }
    });

    // Reset state
    isWidgetMountedRef.current = false;
    mountAttemptsRef.current = 0;
    
  }, [log, toggleWidget, isWidgetVisible]);

  // Mount widget with retry logic
  const mountWidget = useCallback(() => {
    // Don't mount if already mounted
    if (isWidgetMountedRef.current) {
      return;
    }

    // Validate configuration before mounting
    if (!validateConfiguration()) {
      return;
    }

    mountAttemptsRef.current += 1;

    const attemptMount = () => {
      // Check if mount function is available
      if (!window.mountChainlitWidget) {
        if (mountAttemptsRef.current < 10) {
          mountTimeoutRef.current = setTimeout(attemptMount, 500);
        } else {
        }
        return;
      }

      try {

        // Call the official mount function
         window.mountChainlitWidget({
          chainlitServer: import.meta.env.VITE_CHATBOT_URL,
          accessToken: token,
          theme: "dark"
        });


        // Verify mount success after a delay
        setTimeout(() => {
          const widgetElement = document.getElementById('chainlit-copilot');
          if (widgetElement) {
            isWidgetMountedRef.current = true;
            onWidgetStateChange?.(false); // Widget mounted but not necessarily visible
          } else {
            if (mountAttemptsRef.current < 3) {
              mountTimeoutRef.current = setTimeout(mountWidget, 2000);
            }
          }
        }, 1000);

      } catch (error) {
        if (mountAttemptsRef.current < 3) {
          mountTimeoutRef.current = setTimeout(mountWidget, 2000);
        }
      }
    };

    attemptMount();
  }, [token, userId, validateConfiguration, log, onWidgetStateChange]);

  // Handle background clicks to close widget
  const handleBackgroundClick = useCallback((event: MouseEvent) => {
    if (!enableBackgroundClose || !isWidgetMountedRef.current) {
      return;
    }

    // Check if click target is outside widget
    const target = event.target as Element;
    const widgetElements = document.querySelectorAll('#chainlit-copilot, [id*="chainlit"]');
    
    let clickedOutside = true;
    widgetElements.forEach(element => {
      if (element.contains(target)) {
        clickedOutside = false;
      }
    });

    
    if (clickedOutside && isOpenChainlit()) {
      toggleWidget();
    }
  }, [enableBackgroundClose, log, toggleWidget, isWidgetVisible]);

  // Setup background click listener
  const setupBackgroundClickListener = useCallback(() => {
    if (!enableBackgroundClose) return;

    // Remove existing listener if any
    if (backgroundClickListenerRef.current) {
      document.removeEventListener('mousedown', backgroundClickListenerRef.current, true);
    }

    // Add new listener
    backgroundClickListenerRef.current = handleBackgroundClick;
    document.addEventListener('mousedown', handleBackgroundClick, true);
    
  }, [enableBackgroundClose, handleBackgroundClick, log]);

  // Remove background click listener
  const removeBackgroundClickListener = useCallback(() => {
    if (backgroundClickListenerRef.current) {
      document.removeEventListener('mousedown', backgroundClickListenerRef.current, true);
      backgroundClickListenerRef.current = null;
    }
  }, [log]);

  // Handle user/session changes (logout detection)
  useEffect(() => {
    const userChanged = currentUserIdRef.current !== userId;
    const tokenChanged = currentTokenRef.current !== token;
    
    if (userChanged || tokenChanged) {
      

      if (enableLogout && userChanged) {
        cleanupWidget();
      }

      // Update refs
      currentUserIdRef.current = userId;
      currentTokenRef.current = token;

      // Remount with new credentials
      setTimeout(() => {
        mountWidget();
      }, 1000);
    }
  }, [userId, token, enableLogout, cleanupWidget, mountWidget, log]);

  // Main mount effect
  useEffect(() => {
    

    // Setup background click listener
    setupBackgroundClickListener();

    // Mount widget
    mountWidget();

    // Cleanup function
    return () => {
      removeBackgroundClickListener();
      cleanupWidget();
    };
  }, []); // Empty dependency array - we handle changes in separate effect

  // Expose global functions for external control (for debugging/testing)
  useEffect(() => {
    (window as any).chainlitDebug = {
      getState: () => ({
        isMounted: isWidgetMountedRef.current,
        isVisible: isWidgetVisible(),
        userId: currentUserIdRef.current,
        hasToken: !!currentTokenRef.current,
        mountAttempts: mountAttemptsRef.current
      }),
      inspectDOM: inspectWidgetDOM,
      testVisibility: () => {
        const result = isWidgetVisible();
        return result;
      },
      remount: () => {
        cleanupWidget();
        setTimeout(mountWidget, 1000);
      },
      toggle: toggleWidget,
      cleanup: cleanupWidget,
      // Quick access functions
      findWidgetElements: () => {
        const elements = document.querySelectorAll('*[id*="chainlit"], *[class*="chainlit"]');
        return elements;
      },
      checkMainWidget: () => {
        const main = document.getElementById('chainlit-copilot');
        if (main) {
          return main;
        } else {
          return null;
        }
      },
      // Comprehensive test function
      runFullDiagnostic: () => {
        if (isWidgetMountedRef.current) {
          
          try {
            window.toggleChainlitCopilot();
            setTimeout(() => {
              
              // Toggle back to original state
              setTimeout(() => {
                window.toggleChainlitCopilot();
              }, 500);
            }, 500);
          } catch (error) {
            console.error('   Toggle failed:', error);
          }
        } else {
          console.log('\n4. Widget not mounted - cannot test toggle');
        }
        
      }
    };

    return () => {
      delete (window as any).chainlitDebug;
    };
  }, [log, mountWidget, cleanupWidget, toggleWidget, isWidgetVisible, inspectWidgetDOM]);

  return null;
};

export default ChainlitBot;